'use client';
import Link from 'next/link';
import styles from './AccountNavigation.module.css';
import motionStyles from './GlobalNavigationMotion.module.css';
import { PanelLeftClose } from 'lucide-react';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import controls from '@/components/public-site/PublicSiteControls.module.css';

import { createContext, useContext, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ComponentType, type ReactNode, type HTMLAttributes, type CSSProperties, type ComponentProps } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useGlobalNavigationStore } from '@/lib/stores/global-navigation-store';
import { GLOBAL_NAVIGATION_RAIL_WIDTH_PX, GLOBAL_NAVIGATION_MAX_WIDTH_PX, GLOBAL_NAVIGATION_MIN_EXPANDED_WIDTH_PX } from '@/lib/canvas-shell-layout';
import { cn } from '@/lib/cn';

const ExpandedNavigationContext = createContext(false);

/** The same width drives the rail, route content and project navigation. */
export function GlobalNavigationFrame({ className, style, ...props }: HTMLAttributes<HTMLDivElement>) {
  const pathname = usePathname();
  const collapsed = useGlobalNavigationStore(state => state.collapsed);
  const expandedWidth = useGlobalNavigationStore(state => state.expandedWidth);
  const resizing = useGlobalNavigationStore(state => state.resizing);
  const entryWidth = useRef(collapsed ? GLOBAL_NAVIGATION_RAIL_WIDTH_PX : expandedWidth);
  useLayoutEffect(() => {
    if (!pathname) return;
    const navigation = useGlobalNavigationStore.getState();
    if (pathname.startsWith('/p/') || pathname === '/kagemane' || pathname.startsWith('/kagemane/') || pathname === '/cinema-studio') navigation.collapse();
    else navigation.expand();
  }, [pathname]);
  return <div {...props} data-global-navigation-frame data-resizing={resizing} className={cn(motionStyles.frame, 'md:pl-[var(--global-navigation-width)]', className)}
    style={{ ...style, '--navigation-entry-width': `${entryWidth.current}px`, '--global-navigation-width': `${collapsed ? GLOBAL_NAVIGATION_RAIL_WIDTH_PX : expandedWidth}px` } as CSSProperties} />;
}

function GlobalNavigationResizer() {
  const collapsed = useGlobalNavigationStore(state => state.collapsed);
  const expandedWidth = useGlobalNavigationStore(state => state.expandedWidth);
  const resize = useGlobalNavigationStore(state => state.resize);
  const toggle = useGlobalNavigationStore(state => state.toggle);
  const t = useTranslations('globalNavigation');
  const width = collapsed ? GLOBAL_NAVIGATION_RAIL_WIDTH_PX : expandedWidth;
  const drag = useRef<{ x: number; width: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  useLayoutEffect(() => () => {
    if (drag.current) useGlobalNavigationStore.getState().setResizing(false);
  }, []);
  const stop = () => { drag.current = null; setDragging(false); useGlobalNavigationStore.getState().setResizing(false); };
  return <div role="separator" tabIndex={0} aria-orientation="vertical" aria-label={t('resizeSidebar')}
    aria-controls="global-navigation-links" aria-valuemin={GLOBAL_NAVIGATION_RAIL_WIDTH_PX} aria-valuemax={GLOBAL_NAVIGATION_MAX_WIDTH_PX}
    aria-valuenow={width} aria-valuetext={t('sidebarWidth', { width })} title={t('resizeHint')} data-global-navigation-resizer
    className={cn('absolute inset-y-0 -right-1.5 z-50 hidden w-3 touch-none cursor-col-resize select-none outline-none md:block before:absolute before:inset-y-0 before:left-[5px] before:w-0.5 before:transition-colors hover:before:bg-[#4ABDE9] focus-visible:before:bg-[#4ABDE9]', dragging && 'before:bg-[#4ABDE9]')}
    onPointerDown={event => {
      if (event.button !== 0 || !event.isPrimary) return;
      event.preventDefault(); event.currentTarget.focus();
      drag.current = { x: event.clientX, width };
      event.currentTarget.setPointerCapture(event.pointerId); setDragging(true); useGlobalNavigationStore.getState().setResizing(true);
    }}
    onPointerMove={event => {
      if (drag.current) resize(drag.current.width + event.clientX - drag.current.x);
    }}
    onPointerUp={event => {
      stop();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    }}
    onPointerCancel={stop} onLostPointerCapture={stop} onDoubleClick={toggle}
    onKeyDown={event => {
      const step = event.shiftKey ? 32 : 16;
      const next = { ArrowLeft: collapsed ? width : expandedWidth <= GLOBAL_NAVIGATION_MIN_EXPANDED_WIDTH_PX ? GLOBAL_NAVIGATION_RAIL_WIDTH_PX : width - step,
        ArrowRight: collapsed ? expandedWidth : width + step, Home: GLOBAL_NAVIGATION_RAIL_WIDTH_PX, End: GLOBAL_NAVIGATION_MAX_WIDTH_PX }[event.key];
      if (next === undefined) return;
      event.preventDefault(); resize(next);
    }} />;
}

export function GlobalNavigationRail({ label, brand, primary, secondary, account }: {
  label: string;
  brand: ReactNode;
  primary: ReactNode;
  secondary: ReactNode;
  account: ReactNode;
}) {
  const collapsed = useGlobalNavigationStore(state => state.collapsed);
  const toggle = useGlobalNavigationStore(state => state.toggle);
  const t = useTranslations('globalNavigation');
  return (
    <ExpandedNavigationContext.Provider value={!collapsed}>
    <nav aria-label={label} data-global-navigation-rail data-expanded={!collapsed}
      className={cn(styles.rail, motionStyles.rail, 'absolute inset-y-0 left-0 z-[180] flex w-[var(--global-navigation-width,224px)] flex-col border-r border-[#E8F0F4] bg-[#FAFCFD] py-4 text-[#596879] [@media(max-height:720px)]:py-2 px-2')}>
      <div className={motionStyles.header}>
        <div className={motionStyles.brand}>{brand}</div>
      </div>
      <div id="global-navigation-links" className={cn('flex min-h-0 flex-col gap-1 py-1 [@media(max-height:680px)]:overflow-y-auto [@media(max-height:680px)]:overflow-x-hidden [&>div]:w-full [scrollbar-width:none]', collapsed && 'items-center')}>{primary}</div>
      <div className="min-h-3 flex-1 [@media(max-height:720px)]:min-h-2" />
      <div className={cn('flex shrink-0 flex-col gap-1 border-t border-[#E5E8ED] pt-3 [&>div]:w-full [@media(max-height:720px)]:gap-1 [@media(max-height:720px)]:pt-2', collapsed && 'items-center')}>{secondary}</div>
      <div className={cn('mt-3 flex shrink-0 items-center border-t border-[#E5E8ED] pt-3 [@media(max-height:720px)]:mt-2 [@media(max-height:720px)]:pt-2', collapsed ? 'justify-center' : 'w-full')}>{account}</div>
      <button type="button" data-navigation-toggle onClick={toggle} aria-expanded={!collapsed} aria-controls="global-navigation-links"
        aria-label={t(collapsed ? 'expand' : 'collapse')} title={t(collapsed ? 'expand' : 'collapse')} className={motionStyles.toggle}>
        <PanelLeftClose className="h-4 w-4" aria-hidden />
      </button>
      <GlobalNavigationResizer />
    </nav>
    </ExpandedNavigationContext.Provider>
  );
}

export function RailButton({ label, badge, appearance = 'navigation', material, icon: Icon, component: Button = 'button', linkProps, active, highlight, tooltipSide = 'right', className, ...props }: {
  label: string;
  badge?: string;
  appearance?: 'navigation' | 'offer';
  material?: 'primary' | 'secondary';
  component?: 'button' | ComponentType<ButtonHTMLAttributes<HTMLButtonElement>>;
  linkProps?: Omit<ComponentProps<typeof Link>, 'children' | 'className'>;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  active?: boolean;
  highlight?: boolean;
  tooltipSide?: 'left' | 'right';
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const expanded = useContext(ExpandedNavigationContext);
  const offer = appearance === 'offer';
  const surfaceMaterial = material ?? (active && !offer ? 'secondary' : undefined);
  const accessibleLabel = badge ? `${label}, ${badge}` : label;
  const attributes = {
    ...Object.fromEntries(Object.entries(props).filter(([key]) => key.startsWith('aria-') || key.startsWith('data-'))),
    'data-navigation-material': surfaceMaterial,
    title: accessibleLabel,
    'aria-label': accessibleLabel,
    'aria-current': active ? 'page' as const : undefined,
    className: cn(styles.railButton, motionStyles.button, surfaceMaterial && [controls.button, controls[surfaceMaterial]], 'group relative flex mx-auto h-10 w-10 shrink-0 [@media(max-height:720px)]:h-8 items-center justify-center rounded-xl text-[#596879] transition-colors hover:bg-[#E8EDF2] hover:text-[#263445] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] disabled:cursor-wait disabled:opacity-50',
        'font-medium tracking-[-0.01em]',
        offer && 'h-11 gap-2 rounded-xl border border-[#CDD5DD] bg-[linear-gradient(112deg,#EFF2F5_0%,#E2E7EC_25%,#FAFCFD_48%,#E5EAF0_72%,#EEF2F5_100%)] px-3.5 text-[#34414D] transition-[border-color,color] duration-200 hover:border-[#AFBDC9] hover:text-[#182230] active:border-[#94A5B5] [@media(max-height:720px)]:h-10', className),
  };
  const content = <>
      {surfaceMaterial && <LiquidGlass className={styles.railLens}><span /></LiquidGlass>}
      <span aria-hidden data-rail-icon className={cn('relative inline-flex shrink-0 items-center justify-center', offer && 'w-5 text-[#657584] [&>svg]:h-[18px] [&>svg]:w-[18px]')}>
        <Icon className="h-6 w-6 shrink-0" strokeWidth={offer ? 1.5 : 1.8} aria-hidden />
        {highlight && <span data-rail-highlight className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[#4ABDE9] ring-[1.5px] ring-[#F5F7F9]" />}
      </span>
      <span aria-hidden={!expanded} data-navigation-label className="min-w-0 text-left leading-4">{label}</span>
      {expanded && badge && <span aria-hidden data-navigation-badge className={cn('ml-auto shrink-0 whitespace-nowrap rounded-md bg-[#137EA8] px-1.5 py-1 text-[10px] font-semibold leading-none tracking-normal text-white', offer && 'rounded-none bg-transparent bg-[linear-gradient(180deg,#293E50_0%,#596B7B_44%,#34495B_56%,#293E50_100%)] bg-clip-text p-0 text-[13px] font-semibold tabular-nums tracking-[-0.01em] text-transparent [filter:drop-shadow(0_1px_0_#FFFFFF)]')}>{badge}</span>}
      {!expanded && <span aria-hidden className={cn("pointer-events-none absolute top-1/2 z-[230] -translate-y-1/2 whitespace-nowrap rounded-md bg-[#182230] px-2.5 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100", tooltipSide === 'left' ? 'right-full mr-3' : 'left-full ml-3')}>{accessibleLabel}</span>}
  </>;
  return linkProps
    ? <Link {...attributes} {...linkProps} prefetch={false}>{content}</Link>
    : <Button type="button" {...attributes} {...props}>{content}</Button>;
}
